import React from 'react';
import { Layers, Plus, Trash2, Eye, EyeOff } from 'lucide-react';

const SidePanelEditor = ({ side, panel, onChange }) => {
  const update = (changes) => onChange(side, { ...panel, ...changes });

  const updateDoor = (idx, value) => {
    const doors = [...(panel.doors || [])];
    doors[idx] = value;
    update({ doors });
  };

  const addDoor = () => {
    const doors = [...(panel.doors || [])];
    if (doors.length >= 10) return;
    doors.push(doors.length + 1);
    update({ doors });
  };

  const removeDoor = (idx) => {
    const doors = (panel.doors || []).filter((_, i) => i !== idx);
    update({ doors });
  };

  return (
    <div className="panel-editor-card" data-testid={`panel-editor-${side}`}>
      <div className="panel-editor-head">
        <h4 className="panel-editor-title">
          {side === 'left' ? 'Sol panel' : 'Sağ panel'}
        </h4>
        <button
          onClick={() => update({ enabled: !panel.enabled })}
          className={`panel-toggle-btn ${panel.enabled ? 'panel-toggle-btn-on' : ''}`}
          data-testid={`panel-toggle-${side}`}
        >
          {panel.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
          {panel.enabled ? 'Aktiv' : 'Deaktiv'}
        </button>
      </div>

      <div className="panel-editor-fields">
        <div>
          <label className="title-editor-label">Başlıq</label>
          <input
            type="text"
            className="karma-input text-left w-full"
            value={panel.title || ''}
            onChange={(e) => update({ title: e.target.value })}
            maxLength={60}
            placeholder={side === 'left' ? 'Soy Hafızası' : 'Yaşam Sahneleri'}
            data-testid={`panel-title-${side}`}
          />
        </div>
        <div>
          <label className="title-editor-label">Alt başlıq</label>
          <input
            type="text"
            className="karma-input text-left w-full"
            value={panel.subtitle || ''}
            onChange={(e) => update({ subtitle: e.target.value })}
            maxLength={80}
            placeholder={side === 'left' ? 'Aile Kökleri' : 'Devreler'}
            data-testid={`panel-subtitle-${side}`}
          />
        </div>
      </div>

      <div className="panel-doors-section">
        <div className="flex justify-between items-center mb-2">
          <label className="title-editor-label mb-0">Qapı nömrələri ({(panel.doors || []).length})</label>
          <button
            onClick={addDoor}
            disabled={(panel.doors || []).length >= 10}
            className="admin-btn-secondary text-xs px-2 py-1"
            data-testid={`panel-add-door-${side}`}
          >
            <Plus size={12} /> Qapı əlavə
          </button>
        </div>

        <div className="panel-doors-grid">
          {(panel.doors || []).map((d, i) => (
            <div key={`${side}-door-${i}`} className="panel-door-item" data-testid={`panel-door-${side}-${i}`}>
              <span className="panel-door-pos">#{i + 1}</span>
              <input
                type="text"
                className="panel-door-input"
                value={d}
                onChange={(e) => updateDoor(i, e.target.value)}
                maxLength={5}
                data-testid={`panel-door-input-${side}-${i}`}
              />
              <button
                onClick={() => removeDoor(i)}
                className="panel-door-remove"
                title="Qapını sil"
                data-testid={`panel-door-remove-${side}-${i}`}
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PanelsEditor = ({
  panels,
  backgroundFullscreen,
  paradiseMode,
  hideGroupsBar,
  hideSidePanels,
  onPanelsChange,
  onFullscreenChange,
  onParadiseModeChange,
  onHideGroupsBarChange,
  onHideSidePanelsChange,
}) => {
  const safe = panels || { left: {}, right: {} };

  const handleSideChange = (side, newPanel) => {
    onPanelsChange({ ...safe, [side]: newPanel });
  };

  return (
    <div className="admin-section" data-testid="panels-editor-section">
      <h3 className="admin-section-title flex items-center gap-2">
        <Layers size={18} className="text-purple-400" /> Yan panellər və qapılar
      </h3>
      <p className="admin-section-desc">
        Şəkildəki sol və sağ panelləri redaktə edin. Hər biri ayrıca aç/bağla oluna bilər.
        Qapı nömrələrini istəyə görə dəyişdirin.
      </p>

      {/* Fullscreen toggle */}
      <div className="fullscreen-toggle-row">
        <label className="fullscreen-toggle-label">
          <input
            type="checkbox"
            checked={!!backgroundFullscreen}
            onChange={(e) => onFullscreenChange(e.target.checked)}
            data-testid="bg-fullscreen-toggle"
          />
          <span>Arxa fon şəkli <strong>tam ekran</strong> görsəninsin</span>
        </label>
        <p className="text-xs text-white/40 mt-1">
          Aktiv olduqda ilk arxa fon layer-i bütün ekranı əhatə edir; matris üzərində qalan layer-lər saxlanılır.
        </p>
      </div>

      {/* Paradise mode toggle */}
      <div className="fullscreen-toggle-row" data-testid="paradise-mode-toggle-row"
           style={{ borderColor: 'rgba(251,191,36,0.35)', background: 'rgba(251,191,36,0.05)' }}>
        <label className="fullscreen-toggle-label">
          <input
            type="checkbox"
            checked={!!paradiseMode}
            onChange={(e) => onParadiseModeChange && onParadiseModeChange(e.target.checked)}
            data-testid="paradise-mode-toggle"
          />
          <span style={{ color: '#fde68a' }}>✨ <strong>Cənnət rejimi</strong> — qızılı halo + yuxarı işıq şüaları</span>
        </label>
        <p className="text-xs text-white/40 mt-1">
          Aktiv olduqda bütün dairlərin və qapıların ətrafına qızılı halo, üstündən yuxarıya işıq şüası animasiyası əlavə olunur.
          Qapılar üçün həmçinin yeni şəkillər mövcuddur: <strong>Arka qapı</strong>, <strong>İşıq portalı</strong>, <strong>Mərmər sütunlar</strong>.
        </p>
      </div>

      {/* Hide groups bar toggle */}
      <div className="fullscreen-toggle-row" data-testid="hide-groups-bar-toggle-row">
        <label className="fullscreen-toggle-label">
          <input
            type="checkbox"
            checked={!!hideGroupsBar}
            onChange={(e) => onHideGroupsBarChange && onHideGroupsBarChange(e.target.checked)}
            data-testid="hide-groups-bar-toggle"
          />
          <span>Bağlantı qrupları paneli <strong>gizli</strong> olsun</span>
        </label>
        <p className="text-xs text-white/40 mt-1">
          Aktiv olduqda matrisin altındakı qrup toggle düymələri (qrupları aç/bağla) ekranda göstərilməz.
          İstifadəçilər bütün qrupları default vəziyyətdə görəcək.
        </p>
      </div>

      {/* Hide side panels toggle */}
      <div className="fullscreen-toggle-row" data-testid="hide-side-panels-toggle-row">
        <label className="fullscreen-toggle-label">
          <input
            type="checkbox"
            checked={!!hideSidePanels}
            onChange={(e) => onHideSidePanelsChange && onHideSidePanelsChange(e.target.checked)}
            data-testid="hide-side-panels-toggle"
          />
          <span>Sağ/sol yan panellər (Manipura, HRT) <strong>gizli</strong> olsun</span>
        </label>
        <p className="text-xs text-white/40 mt-1">
          Aktiv olduqda matrisin yanında görünən böyük informasiya panelləri (məsələn HRT 10/8, Manipura 18/4) ekrandan
          tamamilə silinir. Aşağıdakı panel-əsaslı konfiqurasiya saxlanılır.
        </p>
      </div>

      <div className="panels-editor-grid">
        <SidePanelEditor side="left" panel={safe.left || {}} onChange={handleSideChange} />
        <SidePanelEditor side="right" panel={safe.right || {}} onChange={handleSideChange} />
      </div>
    </div>
  );
};

export default PanelsEditor;
